import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import download from 'downloadjs'
import { useCallback, useRef, useState } from 'react'

import { NotificationKeys } from '../../../enums/notificationKeys'
import type { ExportFileResult, ReportExportFormat } from '../types/reportExport'

interface UseExportFileDownloadProps {
  errorMessage: string
  fetchExport: (format: ReportExportFormat) => Promise<ExportFileResult>
  isReady: boolean
  notificationKey: NotificationKeys
  onSuccess?: (format: ReportExportFormat) => void
}

interface UseExportFileDownloadResult {
  downloadExport: (format: ReportExportFormat) => Promise<void>
  isExporting: boolean
}

export const useExportFileDownload = ({
  errorMessage,
  fetchExport,
  isReady,
  notificationKey,
  onSuccess,
}: UseExportFileDownloadProps): UseExportFileDownloadResult => {
  const [exportingFormat, setExportingFormat] = useState<ReportExportFormat | null>(null)
  const isExportingRef = useRef(false)

  const downloadExport = useCallback(
    async (format: ReportExportFormat) => {
      if (!isReady || isExportingRef.current) {
        return
      }

      isExportingRef.current = true
      setExportingFormat(format)

      try {
        const { blob, fileName } = await fetchExport(format)

        download(blob, fileName)
        onSuccess?.(format)
      } catch (error: unknown) {
        trackError(error)
        notify({
          id: notificationKey,
          message: errorMessage,
          variant: 'error',
        })
      } finally {
        isExportingRef.current = false
        setExportingFormat(null)
      }
    },
    [errorMessage, fetchExport, isReady, notificationKey, onSuccess],
  )

  return {
    downloadExport,
    isExporting: exportingFormat !== null,
  }
}
